"use client";

import { Button, Dialog, DialogTitle, Flex, Text, VisuallyHidden } from "@radix-ui/themes";
import Script from "next/script";
import { SessionProvider, useSession } from "next-auth/react";
import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { performSignOut } from "@/utils/signOut";

function Quantum() {
    const { status } = useSession();
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    if (!isMounted || status !== "authenticated") {
        return null;
    }
    if (window.location.origin !== "https://vcc.local.att.com:3000") {
        return (
            <Script id="quantum-script" strategy="afterInteractive">
                {`
                (function() {
                  var qtm = document.createElement('script');
                  qtm.type = 'text/javascript';
                  qtm.async = 1;
                  qtm.src = 'https://cdn.quantummetric.com/qscripts/quantum-attbusinesscenter.js';
                  var d = document.getElementsByTagName('script')[0];
                  !window.QuantumMetricAPI && d.parentNode.insertBefore(qtm, d);
                })();
            `}
            </Script>
        );
    }
}

type SessionTimeoutModalProps = Readonly<{
    visible: boolean;
    secondsLeft: number;
    onContinue: () => void;
    onSignOut: () => void;
}>;

function SessionTimeoutModal({ visible, secondsLeft, onContinue, onSignOut }: SessionTimeoutModalProps) {
    const formatSeconds = (s: number) => {
        if (s <= 0) return "00:00";
        const minutes = Math.floor(s / 60);
        const seconds = s % 60;
        const mm = String(minutes).padStart(2, "0");
        const ss = String(seconds).padStart(2, "0");
        return `${mm}:${ss}`;
    };

    return (
        <Dialog.Root open={visible} onOpenChange={() => {}}>
            <Dialog.Content className="DialogContent" aria-label="Session timeout" style={{ border: "none", padding: 0 }}>
                <VisuallyHidden>
                    <DialogTitle>Session Timeout</DialogTitle>
                </VisuallyHidden>
                <Flex
                    gap="3"
                    direction="column"
                    align="center"
                    style={{
                        background: "white",
                        padding: 28,
                        borderRadius: 16,
                        boxShadow: "0 20px 60px rgba(2,6,23,0.35)",
                        textAlign: "center",
                    }}>
                    <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0b2545", fontFamily: "ATTAleckSans, system-ui, sans-serif" }}>
                        Session timeout
                    </h2>

                    <Text as="div" style={{ margin: 0, color: "#475569", maxWidth: 520 }}>
                        You've been idle for a while. Click Continue to keep your session active or Sign out to end your session.
                    </Text>

                    {/* visible timer line */}
                    <Text as="div" style={{ marginTop: 12, color: "#0b2545", fontWeight: 700 }} aria-live="polite">
                        Automatic sign out in {formatSeconds(secondsLeft)}
                    </Text>

                    <Flex gap="3" justify="end" style={{ marginTop: 16 }}>
                        <Button onClick={onSignOut} variant="ghost" className="rounded-full cursor-pointer" style={{ padding: "10px 18px", fontWeight: 700 }}>
                            Sign out
                        </Button>

                        <Button
                            onClick={onContinue}
                            autoFocus
                            className="rounded-full text-white nav-link bg-att-cobalt-blue cursor-pointer"
                            style={{ padding: "10px 20px", fontWeight: 700 }}>
                            Continue
                        </Button>
                    </Flex>
                </Flex>
            </Dialog.Content>
        </Dialog.Root>
    );
}

function SessionAwareProvider({ children }: PropsWithChildren) {
    // idle timeouts in milliseconds
    const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    const POPUP_TIMEOUT = 5 * 60; // 5 minutes

    const [showModal, setShowModal] = useState(false);
    const [popupSecondsLeft, setPopupSecondsLeft] = useState(POPUP_TIMEOUT);
    const popupIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const idleTimerRef = useRef<any>(null);
    const { data: session, status } = useSession();

    const performFullSignOut = () => {
        const provider = session?.user?.provider;
        performSignOut(provider);
    };

    const handleOnIdle = () => {
        // only show modal for authenticated users
        if (status !== "authenticated") return;
        // show modal and start popup countdown
        setPopupSecondsLeft(POPUP_TIMEOUT);
        setShowModal(true);
        // start interval that counts down seconds
        if (popupIntervalRef.current) {
            globalThis.clearInterval(popupIntervalRef.current);
        }
        popupIntervalRef.current = globalThis.setInterval(() => {
            setPopupSecondsLeft((s) => s - 1);
        }, 1000);
    };

    const onActive = () => {
        // only handle activity for authenticated users
        if (status !== "authenticated") return;
    };

    const idle = useIdleTimer({
        timeout: IDLE_TIMEOUT,
        onIdle: handleOnIdle,
        onActive,
        debounce: 500,
    });

    // keep a ref to idle timer API
    useEffect(() => {
        idleTimerRef.current = idle;
    }, [idle]);

    // If the user becomes unauthenticated, ensure the modal is hidden and intervals cleared
    useEffect(() => {
        if (status !== "authenticated") {
            if (popupIntervalRef.current) {
                globalThis.clearInterval(popupIntervalRef.current);
                popupIntervalRef.current = null;
            }
            setShowModal(false);
        }
    }, [status]);

    // react to popupSecondsLeft reaching 0
    useEffect(() => {
        if (!showModal) return;
        if (popupSecondsLeft <= 0) {
            // clear interval then sign out
            if (popupIntervalRef.current) {
                globalThis.clearInterval(popupIntervalRef.current);
                popupIntervalRef.current = null;
            }
            setShowModal(false);
            // perform full sign out flow
            performFullSignOut();
        }
    }, [popupSecondsLeft, showModal]);

    const handleContinue = () => {
        // reset idle timer and hide modal
        idleTimerRef.current?.reset();
        setShowModal(false);
        if (popupIntervalRef.current) {
            globalThis.clearInterval(popupIntervalRef.current);
            popupIntervalRef.current = null;
        }
    };

    const handleSignOut = () => {
        if (popupIntervalRef.current) {
            globalThis.clearInterval(popupIntervalRef.current);
            popupIntervalRef.current = null;
        }
        setShowModal(false);
        performFullSignOut();
    };

    return (
        <>
            <Quantum />
            <SessionTimeoutModal visible={showModal} secondsLeft={popupSecondsLeft} onContinue={handleContinue} onSignOut={handleSignOut} />
            {children}
        </>
    );
}

export default function AuthProvider({ children }: PropsWithChildren) {
    return (
        <SessionProvider>
            <SessionAwareProvider>{children}</SessionAwareProvider>
        </SessionProvider>
    );
}
